import { moment } from "@msuite/utils/date";
import React, { useMemo, useState } from "react";
import { Button } from "../../button";
import { Icon } from "../../icons";
import {
	ActivityChartCell,
	type ActivityChartCellProps,
	type ActivityChartData,
} from "./activity-chart-cell";
import { ActivityChartWeekCell } from "./activity-chart-week-cell";

const NUMBER_OF_COLUMNS = 52;
const NUMBER_OF_ROWS = 7;

/** Props Interface */
interface ActivityChartProps<T>
	extends Pick<ActivityChartCellProps<T>, "onDateClick" | "renderTooltip"> {
	data: Array<ActivityChartData<T>>;
}

function ActivityChartComponent<T>({ data, onDateClick, renderTooltip }: ActivityChartProps<T>) {
	/** State */

	const [baseMoment, setBaseMonth] = useState(moment().endOf("week"));
	const startMoment = baseMoment.clone().subtract(NUMBER_OF_COLUMNS, "weeks").startOf("week");
	const columnArray = Array.from({ length: NUMBER_OF_COLUMNS }, (_, i) => i);
	const rowArray = Array.from({ length: NUMBER_OF_ROWS }, (_, i) => i);

	/** Functions */
	function handleOnGoBack() {
		const newBaseMoment = baseMoment.clone().subtract(1, "weeks");
		setBaseMonth(newBaseMoment);
	}

	function handleOnGoForward() {
		const newBaseMoment = baseMoment.clone().add(1, "weeks");
		setBaseMonth(newBaseMoment);
	}

	const maxValue = useMemo(() => {
		const dates = data.map((item) => item.date);
		const records: Record<string, number> = {};
		dates.forEach((date) => {
			const key = date.toString();
			records[key] = (records[key] || 0) + 1;
		});
		const max = Math.max(...Object.values(records));
		return max > 0 ? max : 1; // Ensure at least 1 to
	}, [data]);

	return (
		<div
			className="relative gap-0 grid w-full activity-chart-container"
			style={{
				gridTemplateColumns: `repeat(${NUMBER_OF_COLUMNS}, 1fr)`,
				gridTemplateRows: "max-content",
			}}
		>
			<div className="-top-1.5 left-0 absolute flex flex-row justify-between w-full">
				<div className="h-stack">
					<Button
						icon={<Icon name="ChevronLeft" />}
						variant="plain"
						onClick={handleOnGoBack}
					/>
					<p className="text-xs">{startMoment.format("WW/YYYY")}</p>
				</div>
				<div className="h-stack">
					<p className="text-xs">{baseMoment.format("WW/YYYY")}</p>
					<Button
						icon={<Icon name="ChevronRight" />}
						variant="plain"
						onClick={handleOnGoForward}
					/>
				</div>
			</div>
			{columnArray.map((_, columnIndex) => {
				const week = startMoment.clone().add(columnIndex, "weeks").startOf("week");
				return (
					<ActivityChartWeekCell key={week.dbWeek()}>
						<div className="w-full aspect-square" />
						<div className="hidden -top-1.5 left-0 absolute activity-chart-week-label">
							<p className="font-mono text-xs">{week.endOf("week").format("WW/YYYY")}</p>
						</div>
						{rowArray.map((_, rowIndex) => {
							const date = baseMoment
								.clone()
								.subtract(NUMBER_OF_COLUMNS - columnIndex - 1, "weeks")
								.startOf("week")
								.add(rowIndex, "days");
							return (
								<ActivityChartCell
									key={date.db()}
									data={data}
									date={date.db()}
									onDateClick={onDateClick}
									renderTooltip={renderTooltip}
									maxValue={maxValue}
								/>
							);
						})}
					</ActivityChartWeekCell>
				);
			})}
		</div>
	);
}

export const ActivityChart = React.memo(
	ActivityChartComponent,
	(prevProps, nextProps) => prevProps.data.length === nextProps.data.length,
) as typeof ActivityChartComponent;
